/**
 * API key format: `spk_` + 32 random base62 characters + a 6-character
 * checksum (CRC-32 of the random part, base62) — 42 characters.
 *
 * The prefix makes a leaked key easy to recognise (and to grep for); the
 * checksum lets the server refuse a mistyped or made-up key without a
 * database round trip. 32 base62 characters are ~190 bits of entropy, so the
 * server stores a plain SHA-256 of the key (no salt is needed for random keys
 * that long). Client-safe on purpose: the dashboard uses `isWellFormedApiKey`
 * to check a pasted key before sending it.
 */

export const API_KEY_PREFIX = "spk_";
export const API_KEY_RANDOM_LENGTH = 32;
const CHECKSUM_LENGTH = 6;
export const API_KEY_LENGTH = API_KEY_PREFIX.length + API_KEY_RANDOM_LENGTH + CHECKSUM_LENGTH;

export const BASE62 = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const BASE62_RE = /^[0-9A-Za-z]+$/;

let crcTable: Uint32Array | null = null;

function table(): Uint32Array {
  if (crcTable) return crcTable;
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  crcTable = t;
  return t;
}

/** CRC-32 (IEEE) of an ASCII string. */
export function crc32(input: string): number {
  const t = table();
  let crc = 0xffffffff;
  for (let i = 0; i < input.length; i++) {
    crc = t[(crc ^ input.charCodeAt(i)) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** Base62 of a non-negative integer, left-padded with "0" to `width`. */
export function toBase62(value: number, width: number): string {
  let n = Math.floor(value);
  let out = "";
  do {
    out = BASE62[n % 62] + out;
    n = Math.floor(n / 62);
  } while (n > 0);
  return out.padStart(width, "0");
}

/** The checksum the key's random part must end with. */
export function apiKeyChecksum(random: string): string {
  return toBase62(crc32(random), CHECKSUM_LENGTH);
}

/** Prefix, length, alphabet and checksum. Says nothing about whether the key exists. */
export function isWellFormedApiKey(key: string): boolean {
  if (key.length !== API_KEY_LENGTH || !key.startsWith(API_KEY_PREFIX)) return false;
  const body = key.slice(API_KEY_PREFIX.length);
  if (!BASE62_RE.test(body)) return false;
  const random = body.slice(0, API_KEY_RANDOM_LENGTH);
  return body.slice(API_KEY_RANDOM_LENGTH) === apiKeyChecksum(random);
}

/**
 * What the dashboard shows to tell keys apart, stored next to the hash:
 * `spk_AbCd…x9Zq`. Four random characters and the last four (checksum)
 * characters — 168 bits of the key stay secret.
 */
export function apiKeyHint(key: string): string {
  return `${key.slice(0, API_KEY_PREFIX.length + 4)}…${key.slice(-4)}`;
}
